import classnames from 'classnames'
import * as React from 'react'
import * as Reakit from 'reakit/Menu'

import { Button, ButtonProps } from '../button'
import { Icon, isValidIconName } from '../icon'
import { PropsWithAs } from '../types'
import { DropdownContext } from './hooks'

const DEFAULT_ELEMENT = 'button'

interface DropdownItemProps extends ButtonProps {
  subtitle?: React.ReactNode

  /**
   * Whether clicking the item should also close the dropdown. Only applies to
   * items without a submenu
   *
   * @default true
   */
  closeOnClick?: boolean
}

function DropdownItemComponent<TagType extends React.ElementType = typeof DEFAULT_ELEMENT>(
  {
    as: asComponent,
    state,
    subtitle,
    closeOnClick = true,
    children,
    rounding,
    ...props
  }: PropsWithAs<TagType> & DropdownItemProps,
  ref: React.ForwardedRef<HTMLButtonElement>
) {
  const context = React.useContext(DropdownContext)

  return (
    <Reakit.MenuItem ref={ref} {...context?.parent} {...props}>
      {(itemProps) => {
        const active = itemProps['aria-selected'] && !context?.parent.animating
        const hasSubmenu = Boolean(itemProps['aria-haspopup'])

        const iconTheme = {
          'text-gray-300': !active,
          'text-gray-white dark:text-white': active,
        }

        return (
          <Button
            {...(itemProps as any)}
            as={asComponent}
            state={state}
            theme={active ? 'menuItem' : 'transparent'}
            active={active}
            rounding={rounding ?? 'small'}
            className={classnames({ 'px-2 py-1 text-2xs': subtitle })}
            contentClass="font-normal"
            size={subtitle ? 'none' : 'regular'}
            leftContent={
              isValidIconName(props.leftContent) ? (
                <Icon
                  name={props.leftContent}
                  className={classnames(iconTheme, 'mr-1', { 'mt-px self-start': subtitle })}
                />
              ) : (
                props.leftContent
              )
            }
            rightContent={
              isValidIconName(props.rightContent) ? (
                <Icon name={props.rightContent} className={classnames(iconTheme, 'ml-1')} />
              ) : (
                props.rightContent
              )
            }
            onClick={(ev: React.MouseEvent) => {
              // Close if we clicked on an item which doesn’t have a submenu
              if (closeOnClick && !hasSubmenu) {
                context?.root.hide()
              }

              itemProps.onClick?.(ev)
            }}
          >
            {children}
            {subtitle && <div className="-mt-1 text-3xs text-gray-500 dark:text-gray-400">{subtitle}</div>}
          </Button>
        )
      }}
    </Reakit.MenuItem>
  )
}

/**
 * We need to forward ref to the function, but typescript looses the type of the
 * props param so we need to typecast to get suggestions to work
 */
export const DropdownItem = React.forwardRef(DropdownItemComponent) as <
  TagType extends React.ElementType = typeof DEFAULT_ELEMENT
>(
  props: PropsWithAs<TagType> & DropdownItemProps & { ref?: React.ForwardedRef<HTMLElement> }
) => ReturnType<typeof DropdownItemComponent>
